import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  DocumentCounts,
  DocumentRow,
  FileType,
  Paginated,
  WorkflowState,
} from "@/lib/types";

interface DocumentsQuery {
  search?: string;
  fileType?: FileType;
  scope?: "my" | "all";
  /**
   * "shared"  → drop owner_only rows (everyone's library)
   * "private" → only owner_only rows, owned by me (private vault)
   * undefined → no audience filter (legacy / admin views)
   */
  audience?: "shared" | "private";
  workflowState?: WorkflowState;
  tag?: number;
  page?: number;
  ordering?: string;
}

/**
 * Hook over GET /api/v1/documents/. Forwards every filter param the
 * DRF viewset supports, keeps previous data on refetch (so paging /
 * filter changes don't flash an empty table).
 */
export function useDocuments(q: DocumentsQuery = {}) {
  return useQuery<Paginated<DocumentRow>>({
    queryKey: ["documents", q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (q.search)        params.set("search",         q.search);
      if (q.fileType)      params.set("file_type",      q.fileType);
      if (q.scope === "my") params.set("scope",         "my");
      if (q.audience)      params.set("audience",       q.audience);
      if (q.workflowState) params.set("workflow_state", q.workflowState);
      if (q.tag != null)   params.set("tag",            String(q.tag));
      if (q.page)          params.set("page",           String(q.page));
      if (q.ordering)      params.set("ordering",       q.ordering);

      const { data } = await api.get<Paginated<DocumentRow>>(
        `/documents/?${params.toString()}`,
      );
      return data;
    },
  });
}

/** Cheap aggregate for the dashboard cards. */
export function useDocumentCounts() {
  return useQuery<DocumentCounts>({
    queryKey: ["documents", "counts"],
    queryFn: async () => {
      const { data } = await api.get<DocumentCounts>("/documents/counts/");
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Richer analytics — three buckets used by the dashboard charts. All
// scoped to what the actor can see.
// ---------------------------------------------------------------------------

export interface DocumentAnalytics {
  by_file_type:      Partial<Record<FileType, number>>;
  by_workflow_state: Partial<Record<WorkflowState, number>>;
  activity_30d:      { date: string; count: number }[];
}

export function useDocumentAnalytics() {
  return useQuery<DocumentAnalytics>({
    queryKey: ["documents", "analytics"],
    queryFn: async () => {
      const { data } = await api.get<DocumentAnalytics>("/documents/analytics/");
      return data;
    },
    staleTime: 60_000,
  });
}
